import { AttributeFormState } from '@idw-types/state/attribute-form-state';
import { useSelector } from 'react-redux';
import { FormikErrors } from 'formik';
import { getAvailableCountries, getCountryCode } from '@redux/selectors/verification-configuration-selectors';
import { FocusEvent, useEffect, useMemo, useState } from 'react';
import { Logger } from '@utils/logger';
import countryRegionData, { Country } from 'country-region-data';
import { SelectOption } from '@iddataweb/idw-common-ui/types';
import { AXNUISelectInputWithErrorMessage } from '@iddataweb/idw-common-ui/containers';
import { getLanguageContent } from '@redux/selectors/language-selectors';

type CountrySelectInputProps = {
	title: string;
	attribute: AttributeFormState;
	fieldName: string;
	fieldTouched: boolean;
	fieldError: string;
	fieldIsInvalid: boolean;
	fieldShouldBeHighlighted: boolean;
	setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<any>>;
	setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
	onBlur: (event: FocusEvent<HTMLInputElement>) => void;
	onSetSubmitValue: (data: any) => void;
	isSubmitting: boolean;
};

export const CountrySelectInput = (props: CountrySelectInputProps) => {
	const {
		title,
		attribute,
		fieldName,
		fieldTouched,
		fieldError,
		fieldIsInvalid,
		fieldShouldBeHighlighted,
		setFieldValue,
		setFieldTouched,
		onBlur,
		onSetSubmitValue,
		isSubmitting
	} = props;

	const content = useSelector(getLanguageContent);
	const availableCountries = useSelector(getAvailableCountries);
	const countryCode = useSelector(getCountryCode);

	const getCountryListMap = (countryList: Array<Country>) => {
		return countryList.map(function (row) {
			return { value: row.countryShortCode, label: row.countryName };
		});
	};

	const countryOptions = useMemo(() => {
		return availableCountries.length > 0 ? availableCountries : getCountryListMap(countryRegionData);
	}, [availableCountries, countryRegionData, getCountryListMap]);

	const getInitialSelectedOption = () => {
		if (attribute.inputData.country) {
			const option = countryOptions.find(co => co.value === attribute.inputData.country);
			if (option) {
				return option;
			}
		}
		return null;
	};

	const [selectedOption, setSelectedOption] = useState(getInitialSelectedOption());

	useEffect(() => {
		if (!selectedOption && countryCode) {
			const initialCountrySelected = countryOptions.find((country) => country.value === countryCode);
			if (initialCountrySelected) {
				handleChange(initialCountrySelected);
			}
		}
	}, [countryCode]);

	useEffect(() => {
		if (!attribute.inputData.country) {
			setSelectedOption(null);
		} else {
			const option = countryOptions.find(country => country.value === attribute.inputData.country);
			if (option) {
				setSelectedOption(option);
			}
		}
	}, [attribute.inputData.country, countryCode]);

	const handleChange = async (newValue: SelectOption) => {
		Logger.info("[CountryAttribute] Selected Country: ", newValue);
		if (newValue) {
			const { value } = newValue;
			onSetSubmitValue({ inputData: { country: value } });
			await setFieldValue(fieldName, value);
			setFieldTouched(fieldName, true, true);
		} else {
			onSetSubmitValue({ inputData: { country: "" } });
			await setFieldValue(fieldName, "");
			setFieldTouched(fieldName, true, true);
		}
	};

	return (
		<AXNUISelectInputWithErrorMessage
			controlId={`attr${attribute.id}-country-input`}
			automationId={`attr${attribute.id}-country-input`}
			ariaLabel={content.validation.country.required}
			isClearable={attribute.isOptional}
			isSearchable={true}
			title={title}
			placeholder={attribute.placeholder
				? attribute.placeholder
				: content.validation.country.required}
			selectedOption={selectedOption}
			options={countryOptions}
			fieldName={fieldName}
			fieldTouched={fieldTouched}
			fieldError={fieldError}
			fieldIsInvalid={fieldIsInvalid}
			fieldShouldBeHighlighted={fieldShouldBeHighlighted}
			onBlur={onBlur}
			setFieldTouched={setFieldTouched}
			onChange={handleChange}
			isDisabled={attribute.isDisabled}
			isPlaceholder={attribute.isPlaceholder}
			isOptional={attribute.optional}
			isSubmitting={isSubmitting}
		/>
	);
};